/**
 * GDPR-Compliant Cookie Consent Component
 * Complies with: UK GDPR, EU GDPR, ePrivacy Directive, ICO & EDPB guidance
 * 
 * Features:
 * - Blocks content until consent decision is made
 * - Three equal buttons: Accept All, Decline, Customize
 * - Granular cookie category control (inline, not separate modal)
 * - Consent logging with timestamps
 * - Ability to modify consent at any time
 * - Non-essential cookies blocked by default
 */

import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Cookie, Shield, Settings, ChevronDown, ChevronUp, Lock, Info, ArrowLeft } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'gdpr_cookie_consent';
const COOKIE_CONSENT_VERSION = '2.0'; // Increment when policy changes require re-consent

// Cookie categories with descriptions
const COOKIE_CATEGORIES = {
  necessary: {
    id: 'necessary',
    name: 'Strictly Necessary',
    description: 'These cookies are essential for the website to function properly. They enable core functionality such as security, authentication, and accessibility. These cookies cannot be disabled.',
    required: true,
    defaultEnabled: true,
  },
  analytics: {
    id: 'analytics',
    name: 'Analytics Cookies',
    description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our services.',
    required: false,
    defaultEnabled: false,
  },
  marketing: {
    id: 'marketing',
    name: 'Marketing Cookies',
    description: 'These cookies are used to track visitors across websites to display relevant advertisements. They may be set by us or by third-party advertising partners.',
    required: false,
    defaultEnabled: false,
  },
  functional: {
    id: 'functional',
    name: 'Functional Cookies',
    description: 'These cookies enable enhanced functionality and personalization, such as remembering your preferences and settings. The website may not function optimally without them.',
    required: false,
    defaultEnabled: false,
  },
};

// Get current consent from localStorage
export const getConsent = () => {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      const consent = JSON.parse(stored);
      // Check if consent version matches current version
      if (consent.version === COOKIE_CONSENT_VERSION) {
        return consent;
      }
    }
  } catch (e) {
    console.error('Error reading cookie consent:', e);
  }
  return null;
};

// Check if specific category is consented
export const hasConsent = (category) => {
  const consent = getConsent();
  if (!consent) return false;
  if (category === 'necessary') return true; // Always allowed
  return consent.categories?.[category] === true;
};

// Log consent to backend (for compliance records)
const logConsent = async (consentData) => {
  try {
    // Store consent log locally as backup
    const logs = JSON.parse(localStorage.getItem('gdpr_consent_logs') || '[]');
    logs.push({
      ...consentData,
      userAgent: navigator.userAgent,
      url: window.location.href,
    });
    // Keep only last 10 logs
    if (logs.length > 10) logs.shift();
    localStorage.setItem('gdpr_consent_logs', JSON.stringify(logs));
  } catch (e) {
    console.error('Error logging consent:', e);
  }
};

// Cookie Consent Provider Component
const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [view, setView] = useState('main'); // 'main' or 'customize'
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  });

  // Check consent on mount - show immediately if no consent
  useEffect(() => {
    const consent = getConsent();
    if (!consent) {
      setShowBanner(true);
      // Prevent scrolling when banner is shown
      document.body.style.overflow = 'hidden';
    } else {
      // Load saved preferences
      setPreferences(prev => ({
        ...prev,
        ...consent.categories,
        necessary: true, // Always true
      }));
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Save consent
  const saveConsent = useCallback((categories, action) => {
    const consentData = {
      version: COOKIE_CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      action: action, // 'accept_all', 'decline', 'customize'
      categories: {
        necessary: true, // Always true
        analytics: categories.analytics || false,
        marketing: categories.marketing || false,
        functional: categories.functional || false,
      },
    };

    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    logConsent(consentData);
    
    setPreferences(consentData.categories);
    setShowBanner(false);
    setView('main');
    document.body.style.overflow = '';

    // Trigger event for other components to react
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: consentData }));
  }, []);

  // Handle Accept All
  const handleAcceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    }, 'accept_all');
  };

  // Handle Decline (only necessary cookies)
  const handleDecline = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    }, 'decline');
  };

  // Handle Save Preferences from customize view
  const handleSavePreferences = () => {
    saveConsent(preferences, 'customize');
  };

  // Toggle category preference
  const toggleCategory = (categoryId) => {
    if (categoryId === 'necessary') return; // Cannot toggle necessary
    setPreferences(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // If banner is not shown, return null (content is accessible)
  if (!showBanner) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="cookie-consent-overlay"
    >
      <div 
        className="w-full max-w-2xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden max-h-[90vh] overflow-y-auto"
        data-testid="cookie-consent-banner"
      >
        {/* MAIN VIEW */}
        {view === 'main' && (
          <>
            {/* Header */}
            <div className="p-6 pb-4 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Cookie className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  Your Privacy Matters
                </h2>
              </div>
              
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                We use cookies and similar tracking technologies to provide, maintain and improve our services. 
                Certain cookies are essential for the website to operate and do not require your consent. 
                Optional cookies, including analytics and advertising cookies, are used only if you provide your consent.
              </p>
              
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-3">
                You can manage your preferences at any time. For further information about how we process personal data, please review our{' '}
                <a 
                  href="/privacy-policy" 
                  className="text-[#0047AB] hover:underline font-medium"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Notice
                </a>.
              </p>
            </div>

            {/* Cookie Categories Summary */}
            <div className="p-6 pt-4 pb-4 bg-slate-50 dark:bg-slate-800/50">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <Lock className="w-4 h-4 mx-auto mb-1 text-green-600" />
                  <p className="text-xs font-medium text-slate-700 dark:text-slate-300">Necessary</p>
                  <p className="text-xs text-green-600">Always Active</p>
                </div>
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <Info className="w-4 h-4 mx-auto mb-1 text-blue-600" />
                  <p className="text-xs font-medium text-slate-700 dark:text-slate-300">Analytics</p>
                  <p className="text-xs text-slate-500">Optional</p>
                </div>
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <Info className="w-4 h-4 mx-auto mb-1 text-purple-600" />
                  <p className="text-xs font-medium text-slate-700 dark:text-slate-300">Marketing</p>
                  <p className="text-xs text-slate-500">Optional</p>
                </div>
                <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                  <Settings className="w-4 h-4 mx-auto mb-1 text-orange-600" />
                  <p className="text-xs font-medium text-slate-700 dark:text-slate-300">Functional</p>
                  <p className="text-xs text-slate-500">Optional</p>
                </div>
              </div>
            </div>

            {/* Buttons - Equal Visual Weight */}
            <div className="p-6 pt-4 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleAcceptAll}
                className="flex-1 h-12 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white font-medium"
                data-testid="cookie-accept-all-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Accept All
              </Button>
              <Button
                onClick={handleDecline}
                variant="outline"
                className="flex-1 h-12 border-2 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium"
                data-testid="cookie-decline-btn"
              >
                Decline Optional Cookies
              </Button>
              <Button
                onClick={() => setView('customize')}
                variant="outline"
                className="flex-1 h-12 border-2 border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium"
                data-testid="cookie-customize-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Customise Settings
              </Button>
            </div>
          </>
        )}

        {/* CUSTOMIZE VIEW - Inline in same banner */}
        {view === 'customize' && (
          <>
            {/* Header with Back Button */}
            <div className="p-6 pb-4 border-b border-slate-200 dark:border-slate-800">
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => setView('main')}
                  className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                  data-testid="cookie-back-btn"
                >
                  <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                </button>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                    Cookie Preferences
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Manage your cookie settings below
                  </p>
                </div>
              </div>
            </div>

            {/* Cookie Category Toggles */}
            <div className="p-6 space-y-3">
              {Object.values(COOKIE_CATEGORIES).map((category) => (
                <div 
                  key={category.id}
                  className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden"
                >
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                  >
                    <div className="flex items-center gap-3">
                      <button
                        className="text-slate-500"
                        aria-label={expandedCategory === category.id ? 'Collapse' : 'Expand'}
                      >
                        {expandedCategory === category.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                      <div>
                        <Label className="font-medium cursor-pointer text-slate-800 dark:text-slate-200">
                          {category.name}
                        </Label>
                        {category.required && (
                          <span className="ml-2 text-xs text-green-600 font-medium">Always Active</span>
                        )}
                      </div>
                    </div>
                    <Switch
                      checked={preferences[category.id]}
                      onCheckedChange={() => toggleCategory(category.id)}
                      disabled={category.required}
                      data-testid={`cookie-toggle-${category.id}`}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  
                  {expandedCategory === category.id && (
                    <div className="px-4 pb-4 pt-0 text-sm text-slate-600 dark:text-slate-400 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30">
                      <p className="pt-3">{category.description}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Save Buttons */}
            <div className="p-6 pt-0 flex gap-3">
              <Button
                onClick={handleSavePreferences}
                className="flex-1 h-12 bg-[#0047AB] hover:bg-[#003380] text-white font-medium"
                data-testid="cookie-save-preferences-btn"
              >
                Save Preferences
              </Button>
              <Button
                onClick={() => setView('main')}
                variant="outline"
                className="flex-1 h-12 border-2 border-slate-300 dark:border-slate-600"
              >
                Back
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Footer Cookie Settings Button Component
export const CookieSettingsButton = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  });

  // Load current preferences when opening
  const handleOpen = () => {
    const consent = getConsent();
    if (consent?.categories) {
      setPreferences({
        ...consent.categories,
        necessary: true,
      });
    }
    setShowDialog(true);
  };

  // Save updated preferences
  const handleSave = () => {
    const consentData = {
      version: COOKIE_CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      action: 'modify',
      categories: {
        necessary: true,
        analytics: preferences.analytics || false,
        marketing: preferences.marketing || false,
        functional: preferences.functional || false,
      },
    };

    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    logConsent(consentData);
    setShowDialog(false);
    
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: consentData }));
  };

  // Toggle category preference
  const toggleCategory = (categoryId) => {
    if (categoryId === 'necessary') return;
    setPreferences(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  return (
    <>
      <button
        onClick={handleOpen}
        className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-sm flex items-center gap-1"
        data-testid="cookie-settings-footer-btn"
      >
        <Cookie className="w-4 h-4" />
        Cookie Settings
      </button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Manage Cookie Preferences
            </DialogTitle>
            <DialogDescription>
              Update your cookie preferences below. Changes will take effect immediately.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {Object.values(COOKIE_CATEGORIES).map((category) => (
              <div 
                key={category.id}
                className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden"
              >
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                >
                  <div className="flex items-center gap-3">
                    <button className="text-slate-500">
                      {expandedCategory === category.id ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                    <div>
                      <Label className="font-medium cursor-pointer">
                        {category.name}
                      </Label>
                      {category.required && (
                        <span className="ml-2 text-xs text-green-600 font-medium">Always Active</span>
                      )}
                    </div>
                  </div>
                  <Switch
                    checked={preferences[category.id]}
                    onCheckedChange={() => toggleCategory(category.id)}
                    disabled={category.required}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                
                {expandedCategory === category.id && (
                  <div className="px-4 pb-4 pt-0 text-sm text-slate-600 dark:text-slate-400 border-t border-slate-100 dark:border-slate-800">
                    <p className="pt-3">{category.description}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
            <Button
              onClick={handleSave}
              className="flex-1 bg-[#0047AB] hover:bg-[#003380] text-white"
            >
              Save Preferences
            </Button>
            <Button
              onClick={() => setShowDialog(false)}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Withdraw Consent Button (can be used separately)
export const WithdrawConsentButton = () => {
  const handleWithdraw = () => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    window.location.reload();
  };

  return (
    <button
      onClick={handleWithdraw}
      className="text-red-500 hover:text-red-700 text-sm"
      data-testid="withdraw-consent-btn"
    >
      Withdraw Consent
    </button>
  );
};

export default CookieConsent;
